import React, { useState, useRef, useEffect } from 'react';
import { toast } from '../../components/toast/CustomToast'

const CompanyIntegrationsPage = ({
    integrations,
    userInfo,
    onOpenEventsModal,
    onOpenTracesPage,
    parametersStatus,
    onOpenParamsModal,
    onOpenDocumentationModal
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [supplierSearchTerm, setSupplierSearchTerm] = useState('');
    const [selectedSupplierId, setSelectedSupplierId] = useState(null);

    // Auto-select first supplier on load
    useEffect(() => {
        if (integrations && Array.isArray(integrations) && integrations.length > 0) {
            const suppliers = [...new Map(
                integrations
                    .filter(i => i.supplier)
                    .map(i => [i.supplier.id, { id: i.supplier.id, name: i.supplier.name, logo_url: i.supplier.logo_url }])
            ).values()].sort((a, b) => a.name.localeCompare(b.name));

            if (suppliers.length > 0 && !selectedSupplierId) {
                setSelectedSupplierId(suppliers[0].id);
            }
        }
    }, [integrations, selectedSupplierId]);

    const copyIntegrationIdToClipboard = (integrationId) => {
        navigator.clipboard.writeText(integrationId).then(() => {
            toast.success('ID da integração copiado para área de transferência');
        }).catch(() => {
            toast.error('Erro ao copiar ID da integração');
        });
    };

    // Group integrations by supplier
    const groupIntegrationsBySupplier = (integrations) => {
        return (Array.isArray(integrations) ? integrations : []).reduce((acc, integration) => {
            const supplier = integration.supplier;
            const key = supplier ? supplier.id : 'no-supplier';
            const supplierName = supplier ? supplier.name : 'Fornecedor não especificado';
            const logoUrl = supplier ? supplier.logo_url : null;

            if (!acc[key]) {
                acc[key] = { name: supplierName, logo_url: logoUrl, integrations: [] };
            }
            acc[key].integrations.push(integration);
            return acc;
        }, {});
    };

    // Get unique suppliers list
    const allSuppliers = [...new Map(
        (Array.isArray(integrations) ? integrations : [])
            .filter(i => i.supplier)
            .map(i => [i.supplier.id, { id: i.supplier.id, name: i.supplier.name, logo_url: i.supplier.logo_url }])
    ).values()].sort((a, b) => a.name.localeCompare(b.name));

    // Filter suppliers based on search
    const filteredSuppliers = allSuppliers.filter(supplier =>
        supplier.name.toLowerCase().includes(supplierSearchTerm.toLowerCase())
    );

    // Get selected supplier info
    const selectedSupplier = allSuppliers.find(s => s.id === selectedSupplierId);

    // Get integrations for selected supplier
    const selectedSupplierIntegrations = selectedSupplierId && integrations
        ? integrations.filter(i => i.supplier?.id === selectedSupplierId)
        : [];

    // Filter integrations by search term
    const filteredIntegrations = selectedSupplierIntegrations.filter(integration => {
        const term = searchTerm.toLowerCase();
        return (
            integration.name.toLowerCase().includes(term) ||
            integration.id.toLowerCase().includes(term) ||
            (integration.system?.name && integration.system.name.toLowerCase().includes(term)) ||
            (integration.system?.system_type?.name && integration.system.system_type.name.toLowerCase().includes(term))
        );
    });

    return (
        <div className="space-y-6">
            <div>
                <h2 className="text-2xl font-bold text-gray-800 dark:text-white">Integrações</h2>
                <p className="text-gray-600 dark:text-gray-400 text-sm mt-1">
                    Use o ID da integração no atributo <span className="font-mono font-semibold">subject</span> do CloudEvent
                </p>
            </div>

            {!integrations || !Array.isArray(integrations) || integrations.length === 0 ? (
                <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                    <span className="material-icons text-4xl text-gray-300 block mb-2">bolt</span>
                    <p className="text-gray-500 dark:text-gray-400">Nenhuma integração disponível</p>
                </div>
            ) : (
                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 flex" style={{ height: 'calc(100vh - 180px)', borderRadius: '0.5rem', overflow: 'hidden' }}>
                    {/* Left Column - Suppliers List */}
                    <div className="w-64 border-r border-gray-200 dark:border-gray-700 flex flex-col">
                        {/* Supplier Search */}
                        <div className="p-3 border-b border-gray-200 dark:border-gray-700">
                            <div className="flex items-center gap-2 px-2 py-2 rounded bg-gray-50 dark:bg-gray-700">
                                <span className="material-icons text-gray-400 flex-shrink-0" style={{fontSize: '18px'}}>search</span>
                                <input
                                    type="text"
                                    placeholder="Fornecedor..."
                                    value={supplierSearchTerm}
                                    onChange={(e) => setSupplierSearchTerm(e.target.value)}
                                    className="flex-1 bg-transparent text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none"
                                />
                                {supplierSearchTerm && (
                                    <button
                                        onClick={() => setSupplierSearchTerm('')}
                                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition flex-shrink-0"
                                    >
                                        <span className="material-icons" style={{fontSize: '16px'}}>close</span>
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Suppliers List */}
                        <div className="flex-1 overflow-y-auto">
                            {filteredSuppliers.length === 0 ? (
                                <div className="p-4 text-center text-sm text-gray-500 dark:text-gray-400">
                                    Nenhum fornecedor encontrado
                                </div>
                            ) : (
                                <div className="space-y-1 p-2">
                                    {filteredSuppliers.map(supplier => {
                                        const supplierIntegrationCount = integrations.filter(i => i.supplier?.id === supplier.id).length;
                                        const isSelected = selectedSupplierId === supplier.id;

                                        return (
                                            <button
                                                key={supplier.id}
                                                onClick={() => setSelectedSupplierId(supplier.id)}
                                                className={`w-full px-3 py-2.5 rounded flex items-center gap-2 text-left text-sm transition ${
                                                    isSelected
                                                        ? 'bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800'
                                                        : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                                                }`}
                                            >
                                                {supplier.logo_url ? (
                                                    <img
                                                        src={supplier.logo_url}
                                                        alt={supplier.name}
                                                        className="h-5 w-5 object-contain flex-shrink-0"
                                                        onError={(e) => { e.target.style.display = 'none'; }}
                                                    />
                                                ) : (
                                                    <div className="h-5 w-5 bg-gray-200 dark:bg-gray-600 rounded flex-shrink-0" />
                                                )}
                                                <span className={`flex-1 truncate font-medium ${
                                                    isSelected
                                                        ? 'text-blue-700 dark:text-blue-300'
                                                        : 'text-gray-900 dark:text-white'
                                                }`}>
                                                    {supplier.name}
                                                </span>
                                                <span className={`text-xs font-medium px-2 py-0.5 rounded-full flex-shrink-0 ${
                                                    isSelected
                                                        ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300'
                                                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                                                }`}>
                                                    {supplierIntegrationCount}
                                                </span>
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Right Column - Integrations */}
                    <div className="flex-1 flex flex-col">
                        {!selectedSupplier ? (
                            <div className="flex-1 flex items-center justify-center">
                                <div className="text-center">
                                    <span className="material-icons text-4xl text-gray-300 dark:text-gray-600 block mb-2">arrow_forward</span>
                                    <p className="text-gray-500 dark:text-gray-400">Selecione um fornecedor para visualizar integrações</p>
                                </div>
                            </div>
                        ) : (
                            <>
                                {/* Supplier Header */}
                                <div className="p-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50 flex items-center gap-3">
                                    {selectedSupplier.logo_url ? (
                                        <img
                                            src={selectedSupplier.logo_url}
                                            alt={selectedSupplier.name}
                                            className="h-7 w-7 object-contain flex-shrink-0"
                                            onError={(e) => { e.target.style.display = 'none'; }}
                                        />
                                    ) : (
                                        <div className="h-7 w-7 bg-gray-200 dark:bg-gray-600 rounded flex-shrink-0" />
                                    )}
                                    <div className="flex-1 min-w-0">
                                        <h3 className="font-semibold text-gray-900 dark:text-white truncate">{selectedSupplier.name}</h3>
                                    </div>
                                    <span className="text-xs font-medium px-2 py-1 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 flex-shrink-0">
                                        {selectedSupplierIntegrations.length} integração{selectedSupplierIntegrations.length !== 1 ? 's' : ''}
                                    </span>
                                </div>

                                {/* Search and Table */}
                                <div className="flex-1 flex flex-col overflow-hidden">
                                    {/* Search Field - Sticky */}
                                    <div className="p-3 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
                                        <div className="flex items-center gap-3 px-3 py-2 rounded bg-gray-50 dark:bg-gray-700">
                                            <span className="material-icons text-gray-400 flex-shrink-0" style={{fontSize: '18px'}}>search</span>
                                            <input
                                                type="text"
                                                placeholder="Pesquisar integração..."
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                                className="flex-1 bg-transparent text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none"
                                            />
                                            {searchTerm && (
                                                <button
                                                    onClick={() => setSearchTerm('')}
                                                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition flex-shrink-0"
                                                >
                                                    <span className="material-icons" style={{fontSize: '16px'}}>close</span>
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    {/* Integrations Table */}
                                    {selectedSupplierIntegrations.length === 0 ? (
                                        <div className="flex-1 flex items-center justify-center">
                                            <p className="text-sm text-gray-500 dark:text-gray-400">Nenhuma integração neste fornecedor</p>
                                        </div>
                                    ) : filteredIntegrations.length === 0 ? (
                                        <div className="flex-1 flex items-center justify-center">
                                            <p className="text-sm text-gray-500 dark:text-gray-400">Nenhuma integração encontrada</p>
                                        </div>
                                    ) : (
                                        <div className="flex-1 overflow-x-auto">
                                            <table className="w-full text-sm">
                                                <thead>
                                                    <tr className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700 sticky top-0">
                                                        <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-300">Nome</th>
                                                        <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-300">Sistema</th>
                                                        <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-300">Tipo</th>
                                                        <th className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-300">Status</th>
                                                        <th className="px-4 py-3"></th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {filteredIntegrations.map((integration) => (
                                                        <tr key={integration.id} className="border-b border-gray-100 dark:border-gray-700/60 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition">
                                                            <td className="px-4 py-3">
                                                                <div className="flex items-start gap-2">
                                                                    <span className="material-icons text-gray-400 flex-shrink-0 mt-0.5" style={{fontSize: '18px'}}>bolt</span>
                                                                    <div className="min-w-0 flex-1">
                                                                        <div className="font-medium text-gray-900 dark:text-white truncate">
                                                                            {integration.name}
                                                                        </div>
                                                                        <div className="flex items-center gap-2 mt-1">
                                                                            <span className="text-xs font-mono text-gray-600 dark:text-gray-400 truncate">
                                                                                {integration.id}
                                                                            </span>
                                                                            <button
                                                                                onClick={() => copyIntegrationIdToClipboard(integration.id)}
                                                                                className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition flex-shrink-0"
                                                                                title="Copiar ID"
                                                                            >
                                                                                <span className="material-icons" style={{fontSize: '14px'}}>content_copy</span>
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td className="px-4 py-3">
                                                                {integration.system?.name && (
                                                                    <span className="inline-block px-2 py-1 rounded text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-200">
                                                                        {integration.system.name}
                                                                    </span>
                                                                )}
                                                            </td>
                                                            <td className="px-4 py-3">
                                                                {integration.system?.system_type?.name && (
                                                                    <span className="inline-block px-2 py-1 rounded text-xs font-medium bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-200">
                                                                        {integration.system.system_type.name}
                                                                    </span>
                                                                )}
                                                            </td>
                                                            <td className="px-4 py-3">
                                                                {integration.active ? (
                                                                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200">
                                                                        <span className="w-1.5 h-1.5 bg-green-600 dark:bg-green-400 rounded-full"></span>
                                                                        Ativo
                                                                    </span>
                                                                ) : (
                                                                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300">
                                                                        <span className="w-1.5 h-1.5 bg-gray-600 dark:bg-gray-400 rounded-full"></span>
                                                                        Inativo
                                                                    </span>
                                                                )}
                                                            </td>
                                                            <td className="px-4 py-3">
                                                                <div className="flex gap-2 flex-shrink-0">
                                                                    {parametersStatus && parametersStatus[integration.id]?.has_parameters && (
                                                                        <button
                                                                            onClick={() => onOpenParamsModal && onOpenParamsModal(integration)}
                                                                            className="inline-flex items-center justify-center p-0.5 bg-white dark:bg-gray-800 border border-amber-500 hover:border-amber-600 text-amber-500 hover:text-amber-600 dark:text-amber-400 dark:hover:text-amber-300 dark:border-amber-400 transition relative"
                                                                            title="Configurações"
                                                                        >
                                                                            <span className="material-icons sm">settings</span>
                                                                            {!parametersStatus[integration.id]?.all_required_filled && parametersStatus[integration.id]?.required_parameters > 0 && (
                                                                                <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-red-500 rounded-full animate-pulse"></span>
                                                                            )}
                                                                        </button>
                                                                    )}
                                                                    <button
                                                                        onClick={() => onOpenEventsModal && onOpenEventsModal(integration)}
                                                                        className="inline-flex items-center justify-center p-0.5 bg-white dark:bg-gray-800 border border-blue-500 hover:border-blue-600 text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 dark:border-blue-400 transition"
                                                                        title="Eventos"
                                                                    >
                                                                        <span className="material-icons sm">schedule</span>
                                                                    </button>
                                                                    <button
                                                                        onClick={() => onOpenTracesPage && onOpenTracesPage(integration)}
                                                                        className="inline-flex items-center justify-center p-0.5 bg-white dark:bg-gray-800 border border-purple-500 hover:border-purple-600 text-purple-500 hover:text-purple-600 dark:text-purple-400 dark:hover:text-purple-300 dark:border-purple-400 transition"
                                                                        title="Traces"
                                                                    >
                                                                        <span className="material-icons sm">timeline</span>
                                                                    </button>
                                                                    {integration.documentation_links && integration.documentation_links.length > 0 && (
                                                                        <button
                                                                            onClick={() => onOpenDocumentationModal && onOpenDocumentationModal(integration)}
                                                                            className="inline-flex items-center justify-center p-0.5 bg-white dark:bg-gray-800 border border-green-500 hover:border-green-600 text-green-500 hover:text-green-600 dark:text-green-400 dark:hover:text-green-300 dark:border-green-400 transition"
                                                                            title="Documentação"
                                                                        >
                                                                            <span className="material-icons sm">help_outline</span>
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default CompanyIntegrationsPage;
